import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  TableRow,
  TableCell,
  Collapse,
  Box,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import CollapsedGrid from "../../../Components/ProGrid/CollapsedGrid";
import usePermission from "../../../../utils/customHooks/usePermission";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { R_PROJECTS, R_PROJECTS_CERTIFIED_PAYROLL } from "../../../../utils/permissionReferenceConfig";
import { getApi, postApi } from "../../../../utils/services/index.js";
import { successToast, errorToast } from "../../../../utils/toastHelper.js";
import { MISSINGCOUNT, POST_NON_PERFORMANCE_STATEMENT_GENERATION } from "../../../../utils/services/apiPath.js";
import { useParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { formatCurrency } from "../../../Components/ProGrid/components/FormatCell";
import { GET_WEEKLY_TIMESHEET_HISTORY } from "../../../../utils/services/apiPath.js";
import { REFRESH_EXCEPTIONS } from "../../../../utils/services/apiPath.js";
import { createODataQuery } from "../../../../utils/services/oData.js";

function ProjectCertifiedPayrollTableRow({
  row,
  permission,
  rowData,
  selectedVal,
  setSelectedVal,
  selectedRow,
  setSelectedRow,
  status,
  handleStatus,
  activeDailySheet,
  columns,
  searchString,
  setSearchString,
  rowCount,
  pageSize,
  setPagination,
  currentPage,
  sortModel,
  setSortModel,
  tokenData,
  handleGenerateCPR,
  handlePayrollTimeSheet,
  handleGenerateCprWithType,
  customExportAction,
  gridFeatureMode
}) {
  let isRowExpanded = selectedRow?.weekEndingDate === row.weekEndingDate && selectedRow?.clientGuid === row.clientGuid;

  const handleRowClick = (row, valid, val) => {
    const shouldExpand = selectedVal.id !== val || !isRowExpanded;
    if (shouldExpand) {
      setSelectedRow({ weekEndingDate: row.weekEndingDate, clientGuid: row.clientGuid });
      setSelectedVal({ id: val, bool: valid });
      setPagination({ page: 0, pageSize });
      setSortModel([]);
    } else {
      setSelectedRow(null);
      setSelectedVal({ id: null, bool: true });
    }
  };

  const { "Certified Payroll List": projectCertifiedPayrollPermission } = usePermission(
    subModPermissionSelector(R_PROJECTS, R_PROJECTS_CERTIFIED_PAYROLL),
    ["Certified Payroll List"]
  );

  const hideTimeSheetExport = !projectCertifiedPayrollPermission?.fieldPerObj?.TimeSheet?.canExport;
  const hidePayrollExport = !projectCertifiedPayrollPermission?.fieldPerObj?.Payroll?.canExport;
  const hideExport = activeDailySheet.name === "TimeSheet" ? hideTimeSheetExport : hidePayrollExport;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMsg, setDialogMsg] = useState("");
  const [pendingStatementPayload, setPendingStatementPayload] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { id } = useParams();
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [pendingCprType, setPendingCprType] = useState(null);
  const [openMissingWarning, setOpenMissingWarning] = useState(false);
  const [missingCountInfo, setMissingCountInfo] = useState({
    missingCount: 0,
    totalCount: 0,
    date: null,
    reportType: null
  });

  const activeSortModel = React.useMemo(
    () =>
      sortModel?.orderCol
        ? [{ field: sortModel.orderCol, sort: sortModel.orderByAscending ? "asc" : "desc" }]
        : [{ field: selectedVal?.id === 1 ? "workedDate" : selectedVal?.id === 2 ? "createdDate" : "generatedDate", sort: "desc" }],
    [sortModel?.orderCol, sortModel?.orderByAscending, selectedVal?.id]
  );

  const [fileHistoryAnchor, setFileHistoryAnchor] = useState(null);
  const [fileHistoryModal, setFileHistoryModal] = useState({ type: null, files: [], loading: false, sortOrder: "newest" });

  const handleOpenFileHistory = async (anchorEl, type) => {
    setFileHistoryAnchor(anchorEl);
    const reportType = type === "timesheet" ? 1 : 2;
    setFileHistoryModal({ type, files: [], loading: true, sortOrder: "newest" });

    const url = GET_WEEKLY_TIMESHEET_HISTORY.replace("{projectGuid}", id);
    const filterStr = `weekEndingDate eq '${row.weekEndingDate}'`;
    const query = createODataQuery([], [], filterStr, "", null, 0, 0, false);

    const { data, error } = await getApi(`${url}${query}&reportType=${reportType}`);

    if (error) {
      errorToast("Failed to load uploaded files.");
      setFileHistoryModal(prev => ({ ...prev, loading: false }));
      return;
    }
    setFileHistoryModal(prev => ({ ...prev, files: data || [], loading: false }));
  };

  const handleCloseFileHistory = () => {
    setFileHistoryAnchor(null);
    setFileHistoryModal({ type: null, files: [], loading: false, sortOrder: "newest" });
  };

  const handleToggleSort = () => setFileHistoryModal(prev => ({ ...prev, sortOrder: prev.sortOrder === "newest" ? "oldest" : "newest" }));

  const sortedFiles = React.useMemo(() => {
    return [...fileHistoryModal.files].sort((a, b) => {
      const diff = new Date(a.createdDate) - new Date(b.createdDate);
      return fileHistoryModal.sortOrder === "newest" ? -diff : diff;
    });
  }, [fileHistoryModal.files, fileHistoryModal.sortOrder]);

  const handleFileDownload = url => {
    const link = document.createElement("a");
    link.href = url;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const processFinalGeneration = (type, reportType) => {
    switch (type) {
      case "CPR":
        handleGenerateCPR(row?.weekEndingDate);
        break;
      case "Amended":
        handleGenerateCprWithType(row?.weekEndingDate, reportType);
        break;
      case "Statement":
        handleGenerateNonPerformanceStatement(false);
        break;
      default:
        break;
    }
  };

  const runMissingValidation = async type => {
    let reportType = null;

    switch (type) {
      case "Amended":
        reportType = "AmendedCpr";
        break;
      case "CPR":
        reportType = "CPR";
        break;
      case "Statement":
        reportType = "Statement";
        break;
      default:
        return { proceed: false };
    }

    const url = `${MISSINGCOUNT.replace("{projectId}", id)}?weekEndingDate=${encodeURIComponent(row?.weekEndingDate)}&reportType=${reportType}`;

    const preCheckResponse = await getApi(url);

    if (!preCheckResponse) {
      errorToast("Pre-check failed");
      return { proceed: false };
    }

    const { hasMissingRecords, missingCount, totalCount } = preCheckResponse;

    if (hasMissingRecords) {
      setMissingCountInfo({
        missingCount,
        totalCount,
        date: row?.weekEndingDate,
        reportType
      });
      setOpenMissingWarning(true);
      return { proceed: false };
    }

    return { proceed: true, reportType };
  };

  const handleGenerateWithValidation = async type => {
    if (row?.isTimesheetException || row?.isPayrollSummaryException) {
      setPendingCprType(type);
      setErrorDialogOpen(true);
      return;
    }

    const result = await runMissingValidation(type);
    if (!result.proceed) return;

    processFinalGeneration(type, result.reportType);
  };

  const handleGenerateWithValidationAfterTimesheet = async type => {
    const result = await runMissingValidation(type);
    if (!result.proceed) return;

    processFinalGeneration(type, result.reportType);
  };

  const handleGenerateNonPerformanceStatement = async (isConfirmed = false) => {
    const effectiveProjectId = row?.projectId || row?.project?.id || id;

    const payload = {
      projectId: effectiveProjectId,
      weekEndingDate: row?.weekEndingDate?.trim?.(),
      isConfirmed
    };

    setIsGenerating(true);

    try {
      const response = await postApi(POST_NON_PERFORMANCE_STATEMENT_GENERATION, payload);
      const status = response?.status;
      const successMessage = response?.message ?? "Statement of Non-Performance generated successfully.";
      const errorMessage = response?.error || response?.message || "An error occurred.";

      if (status === true) {
        setOpenDialog(false);
        successToast(successMessage);
        handlePayrollTimeSheet();
      } else {
        setDialogMsg(errorMessage);
        setPendingStatementPayload(payload);
        setOpenDialog(true);
      }
    } catch (error) {
      setDialogMsg(error?.message || "An error occurred.");
      setPendingStatementPayload(null);
      setOpenDialog(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const isCertifiedPayrollAvailable =
    !!row?.certifiedPayroll && (Array.isArray(row.certifiedPayroll) ? row.certifiedPayroll.length > 0 : Object.keys(row.certifiedPayroll).length > 0);

  const handleGenerateOption = type => {
    setAnchorEl(null);
    switch (type) {
      case "CPR":
        handleGenerateCPR(row?.weekEndingDate, "CPR");
        break;
      case "Amended":
        handleGenerateCprWithType(row?.weekEndingDate, "AmendedCpr");
        break;
      case "Statement":
        handleGenerateNonPerformanceStatement(false);
        break;
      default:
        break;
    }
  };

  const handleExceptionRefresh = async () => {
    let payload = {
      weekEndingDate: row.weekEndingDate
    };
    try {
      const url = `${REFRESH_EXCEPTIONS.replace("{projectGuid}", id)}`;
      const resData = await postApi(url, payload);
      successToast("Exceptions refreshed successfully.");
      await handlePayrollTimeSheet();
    } catch (error) {
      errorToast("Failed to refresh exceptions.");
    }
  };

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell component="th" scope="row">
          {row.weekEndingDate}
        </TableCell>
        <TableCell component="th" scope="row">
          {row.clientName}
        </TableCell>
        {permission && (
          <TableCell align="center">
            <div className="certified-payroll_cell">
              {row?.isTimesheetException === true && (
                <button className="certified-payroll_cell_button" onClick={() => handleRowClick(row, false, 1)}>
                  Exception
                </button>
              )}
              {row?.isTimesheetException && "/"}
              <button className="certified-payroll_cell_button" onClick={() => handleRowClick(row, true, 1)}>
                {row.totalHours > 0 || row.totalAmount > 0 ? `${row.totalHours} Hours` : "N/A"}
              </button>
              {(row.totalHours > 0 || row.totalAmount > 0) && (
                <IconButton
                  size="small"
                  aria-label="View timesheet files"
                  onClick={e => handleOpenFileHistory(e.currentTarget.closest(".certified-payroll_cell"), "timesheet")}
                  sx={{ ml: 0.5, color: "#666", "&:hover": { color: "#333" } }}
                >
                  <InfoIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </div>
          </TableCell>
        )}
        {permission && (
          <TableCell align="center">
            <div className="certified-payroll_cell">
              {row?.isPayrollSummaryException && (
                <button className="certified-payroll_cell_button" onClick={() => handleRowClick(row, false, 2)}>
                  Exception/
                </button>
              )}

              <button className="certified-payroll_cell_button" onClick={() => handleRowClick(row, true, 2)}>
                {row.totalHours > 0 || row.totalAmount > 0 ? formatCurrency(row?.totalAmount, true) : "N/A"}
              </button>
              {(row.totalHours > 0 || row.totalAmount > 0) && (
                <InfoIcon
                  sx={{ ml: 0.5, fontSize: 18, cursor: "pointer", color: "#666", "&:hover": { color: "#333" } }}
                  onClick={e => handleOpenFileHistory(e.currentTarget.closest(".certified-payroll_cell"), "payroll")}
                />
              )}
            </div>
          </TableCell>
        )}
        {permission && (
          <TableCell align="center">
            <div className="certified-payroll_cell">
              <span className="certified-payroll_cell_label">{row?.payrollNumber || "N/A"}</span>
            </div>
          </TableCell>
        )}
        {permission && (
          <TableCell align="left">
            <div className="certified-payroll_cell">
              {row?.isCertifiedPayrollSigned && (
                <button className="certified-payroll_cell_button" onClick={() => handleRowClick(row, true, 3)}>
                  Signed
                </button>
              )}
              {row?.isCertifiedPayrollSigned && row?.isCertifiedPayrollNotSigned && "/"}
              {row?.isCertifiedPayrollNotSigned && (
                <button className="certified-payroll_cell_button button1" onClick={() => handleRowClick(row, false, 3)}>
                  Not Signed
                </button>
              )}
              {row?.isManual && row?.isCertifiedPayrollNotSigned && "/"}
              {row?.isManual && (
                <Box sx={{ display: "flex" }}>
                  <button className="certified-payroll_cell_button button1" onClick={() => handleRowClick(row, false, 3)}>
                    Uploaded
                  </button>
                  <Box sx={{ pl: 2 }}>
                    <Tooltip
                      placement="bottom"
                      title={`This Certified Payroll Report was generated outside of the ProScore Partner Portal by ${tokenData?.GlobalClientName} client and that ${tokenData?.GlobalClientName} Client is solely responsible for its contents and accuracy`}
                      arrow
                    >
                      <InfoIcon />
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </div>
          </TableCell>
        )}
        {row?.showGenerate && (
          <TableCell>
            <Button
              variant="contained"
              onClick={handleClick}
              endIcon={<ExpandMoreIcon />}
              size="small"
              disabled={isGenerating}
              sx={{
                backgroundColor: "#2979ff",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#1565c0" }
              }}
            >
              {isGenerating ? <CircularProgress size={20} color="inherit" /> : "Generate"}
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{ sx: { mt: 1, boxShadow: "none", borderRadius: "8px", border: "1px solid #e0e0e0", width: 240 } }}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <>
                <MenuItem
                  disabled={!isCertifiedPayrollAvailable}
                  onClick={() => handleGenerateWithValidation("Amended")}
                  sx={{
                    width: "95%",
                    backgroundColor: "#E9E9E9",
                    borderRadius: "5px",
                    margin: "0 auto 8px auto",
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: ".7rem",
                    color: "#616161",
                    "&:hover": { backgroundColor: "#DCEDFD" }
                  }}
                >
                  Amended CPR
                  <Box
                    className="customArrow"
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      backgroundColor: "#1F88E6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      ml: 1,
                      transition: "background-color 0.3s ease"
                    }}
                  >
                    <ArrowBackIcon sx={{ color: "#fff", fontSize: 10, transform: "rotate(180deg)" }} />
                  </Box>
                </MenuItem>
              </>

              <MenuItem
                onClick={() => handleGenerateWithValidation("CPR")}
                sx={{
                  width: "95%",
                  backgroundColor: "#E9E9E9",
                  borderRadius: "5px",
                  margin: "0 auto 8px auto",
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: ".7rem",
                  color: "#616161",
                  "&:hover": { backgroundColor: "#DCEDFD" }
                }}
              >
                CPR
                <Box
                  className="customArrow"
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "#1F88E6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ml: 1,
                    transition: "background-color 0.3s ease"
                  }}
                >
                  <ArrowBackIcon sx={{ color: "#fff", fontSize: 10, transform: "rotate(180deg)" }} />
                </Box>
              </MenuItem>

              <MenuItem
                onClick={() => handleGenerateOption("Statement")}
                disabled={row?.totalHours > 0 || row?.totalAmount > 0 || row?.isTimesheetException === true}
                sx={{
                  width: "95%",
                  backgroundColor: "#E9E9E9",
                  borderRadius: "5px",
                  margin: "0 auto 8px auto",
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: ".7rem",
                  color: "#616161",
                  "&:hover": { backgroundColor: "#DCEDFD" }
                }}
              >
                Statement of Non-Performance
                <Box
                  className="customArrow"
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "#1F88E6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ml: 1,
                    transition: "background-color 0.3s ease"
                  }}
                >
                  <ArrowBackIcon sx={{ color: "#fff", fontSize: 10, transform: "rotate(180deg)" }} />
                </Box>
              </MenuItem>

              <MenuItem
                onClick={() => handleExceptionRefresh()} // Update this when ready
                disabled={!(row.isTimesheetException || row.isPayrollSummaryException)}
                sx={{
                  width: "95%",
                  backgroundColor: "#E9E9E9",
                  borderRadius: "5px",
                  margin: "0 auto 8px auto",
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: ".7rem",
                  color: "#616161",
                  "&:hover": { backgroundColor: "#DCEDFD" }
                }}
              >
                Refresh Exceptions
                <Box
                  className="customArrow"
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "#1F88E6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ml: 1,
                    transition: "background-color 0.3s ease"
                  }}
                >
                  <ArrowBackIcon sx={{ color: "#fff", fontSize: 10, transform: "rotate(180deg)" }} />
                </Box>
              </MenuItem>
            </Menu>
          </TableCell>
        )}
      </TableRow>
      {isRowExpanded && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={isRowExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1, height: "400px", overflowY: "scroll" }}>
                {rowData ? (
                  <CollapsedGrid
                    title={`${activeDailySheet.name} - ${activeDailySheet.date}`}
                    columns={columns}
                    loading={status}
                    hideAddButton
                    hideSearch={true}
                    hideExport={hideExport}
                    rows={rowData}
                    searchMode={!!searchString}
                    searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
                    customExportAction={customExportAction}
                    options={{
                      getRowId: row => row.id,
                      rowCount: rowCount,
                      paginationMode: gridFeatureMode,
                      paginationModel: { pageSize: pageSize, page: currentPage },
                      onPaginationModelChange: setPagination,
                      sortingMode: gridFeatureMode,
                      sortModel: activeSortModel,
                      onSortModelChange: setSortModel
                    }}
                  />
                ) : (
                  <>{handleStatus()}</>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="statement-dialog-title"
        aria-describedby="statement-dialog-description"
      >
        <DialogTitle id="statement-dialog-title">Message</DialogTitle>
        <DialogContent sx={{ textAlign: "center", p: 3 }}>
          <DialogContentText id="statement-dialog-description">{dialogMsg}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 2 }}>
          {[
            "We already have a timesheet",
            "We already have a payroll summary",
            "We already have a timesheet and payroll summary",
            "Certified Payroll already exists for this Week Ending Date"
          ].some(phrase => dialogMsg?.includes(phrase)) ? (
            <>
              <Button onClick={() => setOpenDialog(false)} color="inherit" variant="outlined" sx={{ pt: 1, pb: 1 }}>
                No, Cancel
              </Button>
              <Button
                onClick={() => {
                  if (pendingStatementPayload) {
                    handleGenerateNonPerformanceStatement(true);
                  }
                  setOpenDialog(false);
                }}
                color="primary"
                variant="contained"
              >
                Yes, Continue
              </Button>
            </>
          ) : (
            <Button onClick={() => setOpenDialog(false)} color="primary" variant="contained">
              Okay
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogContent sx={{ textAlign: "center", p: 3 }}>
          <DialogContentText id="error-dialog-description">
            This pay period data has errors, some information will not be shown in the CPR.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={() => {
              setErrorDialogOpen(false);
              setPendingCprType(null);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErrorDialogOpen(false);
              if (pendingCprType) {
                handleGenerateWithValidationAfterTimesheet(pendingCprType);
                setPendingCprType(null);
              }
            }}
            color="primary"
            variant="contained"
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openMissingWarning} onClose={() => setOpenMissingWarning(false)}>
        <DialogContent sx={{ textAlign: "center", p: 4 }}>
          <DialogContentText>
            <strong>Warning:</strong>
            <br />
            {`${missingCountInfo.missingCount}/${missingCountInfo.totalCount} records are missing in your generated CPR.`}
            <br />
            Do you wish to continue?
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button onClick={() => setOpenMissingWarning(false)} color="inherit" variant="outlined" sx={{ pt: 1, pb: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setOpenMissingWarning(false);

              if (missingCountInfo.reportType === "CPR") {
                handleGenerateCPR(missingCountInfo.date);
              } else if (missingCountInfo.reportType === "Statement") {
                handleGenerateNonPerformanceStatement(false);
              } else {
                handleGenerateCprWithType(missingCountInfo.date, missingCountInfo.reportType);
              }
            }}
          >
            Proceed Anyways
          </Button>
        </DialogActions>
      </Dialog>
      <Popover
        open={Boolean(fileHistoryAnchor)}
        anchorEl={fileHistoryAnchor}
        onClose={handleCloseFileHistory}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { width: 320, p: 2 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box sx={{ fontWeight: 500, fontSize: "0.85rem" }}>Uploaded {fileHistoryModal.type === "timesheet" ? "Timesheet" : "Payroll"} Files</Box>
          <Button size="small" onClick={handleToggleSort}>
            Sort: {fileHistoryModal.sortOrder === "newest" ? "Newest" : "Oldest"}
          </Button>
        </Box>
        <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
          {fileHistoryModal.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : sortedFiles.length === 0 ? (
            <Box sx={{ color: "text.secondary", fontSize: "0.85rem" }}>No files found.</Box>
          ) : (
            sortedFiles.map(file => (
              <Box
                key={file.payrollTimeSheetReportId}
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, py: 1, borderBottom: "1px solid #eee" }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontSize: "0.78rem", wordBreak: "break-word" }}>{file.fileName}</Box>
                  <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{file.createdDate}</Box>
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Download ${file.fileName}`}
                  onClick={() => handleFileDownload(file.ref)}
                  disabled={!file.ref}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
}

ProjectCertifiedPayrollTableRow.propTypes = {
  row: PropTypes.object.isRequired,
  permission: PropTypes.bool.isRequired,
  rowData: PropTypes.array,
  selectedVal: PropTypes.object,
  setSelectedVal: PropTypes.func.isRequired,
  selectedRow: PropTypes.object,
  setSelectedRow: PropTypes.func.isRequired,
  status: PropTypes.bool.isRequired,
  handleStatus: PropTypes.func.isRequired,
  activeDailySheet: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  searchString: PropTypes.string.isRequired,
  setSearchString: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPagination: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  sortModel: PropTypes.object,
  setSortModel: PropTypes.func.isRequired,
  tokenData: PropTypes.object.isRequired,
  handlePayrollTimeSheet: PropTypes.func.isRequired,
  handleGenerateCprWithType: PropTypes.func.isRequired
};

export default ProjectCertifiedPayrollTableRow;
